/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */

// This file powers search within the standalone product.
// The data here is used to generate the list of all routes that are searchable within global search,
// along with search synonyms, and their children.
//
// These routes are specifically curated for standalone workspaces and match the navigation
// structure defined in components/standalone/navbar.ts

import { canSeeCustomAnswers } from 'embercom/helpers/can-see-custom-answers';
import type SearchableRoute from './searchable-route';
import { capitalize } from '@ember/string';
import { SupportedStandalonePlatforms } from 'embercom/objects/standalone/constants';

const standalonePlatformIntegrationRoutes = (platform: string[]) =>
  platform.map((platform) => ({
    route: 'apps.app.settings.standalone.integration',
    name: `${capitalize(platform)} integration`,
    synonyms: ['Connection', 'Sync data'],
    showOnlyOnCondition: (app: $TSFixMe) => app.hasStandalonePlatform(platform),
  }));

const StandaloneRoutes: Array<SearchableRoute> = [
  {
    route: 'apps.app.standalone.reports',
    name: 'Analyze',
    synonyms: ['Analytics', 'Reports', 'Metrics'],
    children: [
      {
        route: 'apps.app.standalone.performance',
        name: 'Performance',
        synonyms: ['Analytics', 'Metrics'],
        showOnlyOnCondition: (app) => app.canSeeAiCsatMetrics,
      },
      {
        route: 'apps.app.standalone.reports',
        name: 'Performance',
        synonyms: ['Reports', 'Analytics', 'Metrics'],
        showOnlyOnCondition: (app) => !app.canSeeAiCsatMetrics,
      },
      {
        route: 'apps.app.standalone.optimize-fin',
        name: 'Optimize',
        synonyms: ['Optimize Fin', 'AI'],
        showOnlyOnCondition: (app) => app.canSeeAiInsightsBeta,
      },
      {
        route: 'apps.app.standalone.topics-explorer',
        name: 'Topics Explorer',
        synonyms: ['Insights'],
        showOnlyOnCondition: (app) => app.canSeeAiInsightsBeta,
      },
      {
        route: 'apps.app.standalone.content-gaps',
        name: 'Unresolved questions',
        synonyms: ['Content Gaps', 'Analysis'],
        showOnlyOnCondition: (app) => !app.canSeeAiInsightsBeta,
      },
      {
        route: 'apps.app.standalone.reports.custom-report',
        name: 'Custom Report',
        synonyms: ['Analytics'],
        showOnlyOnCondition: (app) => app.canSeeAiCsatMetrics,
      },
      {
        route: 'inbox.workspace.inbox.inbox',
        name: 'Conversations',
        synonyms: ['Inbox', 'Messages', 'Chat'],
        models: (app) => {
          return [app.id, 'shared', 'all'];
        },
      },
    ],
  },
  {
    route: 'apps.app.knowledge-hub.overview',
    name: 'Train',
    synonyms: ['Knowledge', 'Hub'],
    children: [
      {
        route: 'apps.app.knowledge-hub.overview',
        name: 'Content',
        synonyms: ['Knowledge', 'Hub', 'Articles'],
      },
      {
        route: 'apps.app.standalone.knowledge.custom-answers',
        name: 'Custom Answers',
        synonyms: ['AI'],
        requiredFeatureFlags: ['team-standalone-custom-answers'],
        showOnlyOnCondition: (app) => canSeeCustomAnswers(app),
      },
      {
        route: 'apps.app.standalone.guidance',
        name: 'Guidance',
        synonyms: ['Guidelines', 'AI'],
        requiredFeatureFlags: ['fin-guidance-closed-beta', 'team-standalone-fin-guidance'],
      },
      {
        route: 'apps.app.standalone.tasks',
        name: 'Tasks',
        synonyms: ['Processes', 'Workflows'],
      },
      {
        route: 'apps.app.standalone.knowledge.suggestions',
        name: 'Suggestions',
        synonyms: ['AI'],
        requiredFeatureFlags: ['conversation-extraction-external-sources'],
      },
    ],
  },
  {
    route: 'apps.app.standalone.testing',
    name: 'Test',
    synonyms: ['Testing', 'Playground'],
    requiredFeatureFlags: ['answerbot-fin-playground'],
  },
  {
    route: 'apps.app.standalone.deploy',
    name: 'Deploy',
    synonyms: ['Deployment', 'Integration'],
    children: [
      {
        route: 'apps.app.standalone.deploy.overview',
        name: 'Overview',
        synonyms: [],
      },
      {
        route: 'apps.app.standalone.deploy.intercom-messenger',
        name: 'Intercom Messenger',
        synonyms: ['Chat'],
        children: [
          {
            route: 'apps.app.standalone.deploy.intercom-messenger.integrate',
            name: 'Connect',
            synonyms: ['Setup', 'Connect'],
          },
          {
            route: 'apps.app.standalone.deploy.intercom-messenger.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
          {
            route: 'apps.app.standalone.deploy.intercom-messenger.install',
            name: 'Install',
            synonyms: ['Setup', 'Implementation'],
          },
          {
            route: 'apps.app.standalone.deploy.intercom-messenger.test',
            name: 'Test',
            synonyms: ['Testing', 'Validation'],
          },
          {
            route: 'apps.app.standalone.deploy.intercom-messenger.go-live',
            name: 'Go Live',
            synonyms: ['Launch', 'Deploy'],
          },
        ],
      },
      {
        route: 'apps.app.standalone.deploy.api',
        name: 'Fin over API',
        synonyms: ['Integration'],
        requiredFeatureFlags: ['team-standalone-fin-over-api'],
        children: [
          {
            route: 'apps.app.standalone.deploy.api.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
        ],
      },
      {
        route: 'apps.app.standalone.deploy.salesforce.cases',
        name: 'Salesforce Cases',
        synonyms: ['CRM'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('salesforce'),
        children: [
          {
            route: 'apps.app.standalone.deploy.salesforce.cases.connect',
            name: 'Connect',
            synonyms: ['Setup', 'Integrate'],
          },
          {
            route: 'apps.app.standalone.deploy.salesforce.cases.configure-emails',
            name: 'Configure Emails',
            synonyms: ['Email Setup'],
          },
          {
            route: 'apps.app.standalone.deploy.salesforce.cases.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
          {
            route: 'apps.app.standalone.deploy.salesforce.cases.assign',
            name: 'Assign',
            synonyms: ['Assignment', 'Routing'],
          },
          {
            route: 'apps.app.standalone.deploy.salesforce.cases.go-live',
            name: 'Go Live',
            synonyms: ['Launch', 'Deploy'],
          },
        ],
      },
      {
        route: 'apps.app.standalone.deploy.zendesk.tickets',
        name: 'Zendesk Tickets',
        synonyms: ['Support'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('zendesk'),
        children: [
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets.integrate',
            name: 'Connect',
            synonyms: ['Setup', 'Integrate'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets.test',
            name: 'Test',
            synonyms: ['Testing', 'Validation'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets.assign',
            name: 'Assign',
            synonyms: ['Assignment', 'Routing'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.tickets.go-live',
            name: 'Go Live',
            synonyms: ['Launch', 'Deploy'],
          },
        ],
      },
      {
        route: 'apps.app.standalone.deploy.zendesk.messaging',
        name: 'Zendesk Messaging',
        synonyms: ['Chat'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('zendesk'),
        children: [
          {
            route: 'apps.app.standalone.deploy.zendesk.messaging.integrate',
            name: 'Connect',
            synonyms: ['Setup', 'Integrate'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.messaging.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.messaging.test',
            name: 'Test',
            synonyms: ['Testing', 'Validation'],
          },
          {
            route: 'apps.app.standalone.deploy.zendesk.messaging.go-live',
            name: 'Go Live',
            synonyms: ['Launch', 'Deploy'],
          },
        ],
      },
      {
        route: 'apps.app.standalone.deploy.freshdesk.tickets',
        name: 'Freshdesk Tickets',
        synonyms: ['Support'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('freshdesk'),
        children: [
          {
            route: 'apps.app.standalone.deploy.freshdesk.tickets.integrate',
            name: 'Connect',
            synonyms: ['Setup', 'Integrate'],
          },
          {
            route: 'apps.app.standalone.deploy.freshdesk.tickets.workflow',
            name: 'Workflow',
            synonyms: ['Process', 'Flow'],
          },
          {
            route: 'apps.app.standalone.deploy.freshdesk.tickets.go-live',
            name: 'Go Live',
            synonyms: ['Launch', 'Deploy'],
          },
        ],
      },
      {
        route: 'apps.app.phone.standalone.deploy',
        name: 'Phone',
        synonyms: ['Call', 'Voice'],
        requiredFeatureFlags: ['team-standalone-deploy-phone'],
      },
      {
        route: 'apps.app.standalone.deploy.zendesk.copilot',
        name: 'Fin Copilot for Zendesk',
        synonyms: ['Zendesk Copilot', 'AI'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('zendesk'),
      },
      {
        route: 'apps.app.standalone.deploy.salesforce.copilot',
        name: 'Fin Copilot for Salesforce',
        synonyms: ['Salesforce Copilot', 'AI'],
        showOnlyOnCondition: (app) => app.hasStandalonePlatform('salesforce'),
        requiredFeatureFlags: ['team-standalone-copilot-salesforce'],
      },
    ],
  },
  {
    route: 'apps.app.settings.index',
    name: 'Settings',
    synonyms: ['Configuration', 'Preferences'],
    children: [
      {
        route: 'apps.app.settings.workspace.general',
        name: 'Workspace',
        synonyms: ['App'],
        children: [
          {
            route: 'apps.app.settings.workspace.general',
            name: 'General',
            synonyms: ['Workspace name', 'Timezone', 'Languages'],
          },
          {
            route: 'apps.app.settings.workspace.teammates',
            name: 'Teammates',
            synonyms: ['Team Members', 'Users', 'Colleagues', 'Invite'],
          },
          {
            route: 'apps.app.settings.workspace.office-hours',
            name: 'Office Hours',
            synonyms: ['Business Hours', 'Work Schedule', 'Availability'],
          },
          {
            route: 'apps.app.settings.workspace.security',
            name: 'Security',
            synonyms: ['Safety', 'Protection', 'Authentication'],
          },
        ],
      },
      {
        route: 'apps.app.settings.subscription.billing',
        name: 'Subscription',
        synonyms: ['Billing', 'Payment', 'Plan'],
        children: [
          {
            route: 'apps.app.billing.summary',
            name: 'Overview',
            synonyms: ['Plan', 'Summary'],
          },
          {
            route: 'apps.app.billing.invoices',
            name: 'Invoices',
            synonyms: ['Payment history'],
          },
          {
            route: 'apps.app.billing.settings',
            name: 'Billing settings',
            synonyms: ['Payment settings', 'Preferences'],
          },
          {
            route: 'apps.app.billing.manage-usage',
            name: 'Fin usage limits',
            synonyms: ['AI limits'],
          },
        ],
      },
      {
        route: 'apps.app.settings.channels.messenger.web',
        name: 'Channels',
        synonyms: ['Communication'],
        children: [
          {
            route: 'apps.app.settings.channels.messenger.web',
            name: 'Messenger',
            synonyms: ['Chat', 'Website', 'Styling', 'Styles'],
          },
        ],
      },
      {
        route: 'apps.app.settings.data.tags',
        name: 'Data',
        synonyms: ['Information'],
        children: [
          {
            route: 'apps.app.settings.data.tags',
            name: 'Tags',
            synonyms: ['Labels', 'Categories'],
          },
          {
            route: 'apps.app.settings.standalone.data.people',
            name: 'People',
            synonyms: ['Contacts', 'Users', 'Customers'],
          },
          {
            route: 'apps.app.settings.standalone.data.companies',
            name: 'Companies',
            synonyms: ['Organizations', 'Businesses'],
          },
          {
            route: 'apps.app.settings.data.conversation-tickets',
            name: 'Conversations',
            synonyms: ['Attributes', 'Custom attributes'],
          },
        ],
      },
      {
        route: 'apps.app.settings.ai-automation.fin-reply-settings',
        name: 'AI & Automation',
        synonyms: [],
        children: [
          {
            route: 'apps.app.settings.ai-automation.fin-reply-settings',
            name: 'Fin Reply Settings',
            synonyms: ['Response settings'],
          },
        ],
      },
      {
        route: 'apps.app.settings.app-settings.custom-actions',
        name: 'Apps',
        synonyms: ['Integrations', 'Store'],
        children: [
          {
            route: 'apps.app.settings.app-settings.custom-actions',
            name: 'Data connectors',
            synonyms: ['Custom Actions', 'Integrations'],
          },
          {
            route: 'apps.app.settings.app-settings.authentication',
            name: 'Authentication',
            synonyms: ['API tokens', 'Access tokens'],
          },
          ...standalonePlatformIntegrationRoutes(SupportedStandalonePlatforms),
        ],
      },
      {
        route: 'apps.app.settings.personal.details',
        name: 'Personal',
        synonyms: ['Profile', 'Account'],
        children: [
          {
            route: 'apps.app.settings.personal.details',
            name: 'Details',
            synonyms: ['Profile', 'Information'],
          },
          {
            route: 'apps.app.settings.personal.account-security',
            name: 'Account security',
            synonyms: ['Password', 'Two Factor'],
          },
          {
            route: 'apps.app.settings.personal.notifications',
            name: 'Notifications',
            synonyms: ['Alerts', 'Email'],
          },
          {
            route: 'apps.app.settings.personal.account-access',
            name: 'Account Access',
            synonyms: ['Revoke Access', 'Permissions'],
          },
        ],
      },
    ],
  },
];

export default StandaloneRoutes;
